import { Entity, UniqueEntityID } from '@domain/core'

import { UserChartBounds } from '@/domain/protocols/UserChart'

export interface UserChartProps {
  name: string
  originalFileName: string
  addedAt: string
  updatedAt?: string
  boundaries: UserChartBounds
  minZoom: number
  maxZoom: number
  // Byte size of the imported source file, captured at upload from `File.size`.
  // Optional: charts imported before this field existed (and server responses until
  // the backend echoes it) have no size, so the list description hides that part.
  fileSizeBytes?: number
  deleted: boolean
  synced: boolean
}

export interface UserChartPojo {
  id: string
  name: string
  originalFilename: string
  addedAt: string
  updatedAt?: string
  boundaries: UserChartBounds
  minZoom: number
  maxZoom: number
  fileSizeBytes?: number
}

export class UserChart extends Entity<UserChartProps, UserChartPojo> {
  private constructor(props: UserChartProps, id?: UniqueEntityID) {
    super(props, id)
  }

  public static create(props: UserChartProps, id?: UniqueEntityID): UserChart {
    return new UserChart(props, id)
  }

  public static fromPojo(pojo: UserChartPojo): UserChart {
    if (!pojo) return null

    const props: UserChartProps = {
      name: pojo.name,
      originalFileName: pojo.originalFilename,
      addedAt: pojo.addedAt,
      updatedAt: pojo.updatedAt,
      boundaries: pojo.boundaries,
      minZoom: pojo.minZoom,
      maxZoom: pojo.maxZoom,
      fileSizeBytes: pojo.fileSizeBytes,
      deleted: false,
      synced: true
    }

    return new UserChart(props, new UniqueEntityID(pojo.id))
  }

  public toPojo(): UserChartPojo {
    return {
      id: this.id.toString(),
      name: this.props.name,
      originalFilename: this.props.originalFileName,
      addedAt: this.props.addedAt,
      updatedAt: this.props.updatedAt,
      boundaries: this.props.boundaries,
      minZoom: this.props.minZoom,
      maxZoom: this.props.maxZoom,
      fileSizeBytes: this.props.fileSizeBytes
    }
  }

  public get name(): string {
    return this.props.name
  }

  public set name(name: string) {
    this.props.name = name
  }

  public get originalFileName(): string {
    return this.props.originalFileName
  }

  public set originalFileName(originalFileName: string) {
    this.props.originalFileName = originalFileName
  }

  public get addedAt(): string {
    return this.props.addedAt
  }

  public set addedAt(addedAt: string) {
    this.props.addedAt = addedAt
  }

  public get updatedAt(): string | undefined {
    return this.props.updatedAt
  }

  public set updatedAt(updatedAt: string | undefined) {
    this.props.updatedAt = updatedAt
  }

  public get boundaries(): UserChartBounds {
    return this.props.boundaries
  }

  public set boundaries(boundaries: UserChartBounds) {
    this.props.boundaries = boundaries
  }

  public get minZoom(): number {
    return this.props.minZoom
  }

  public set minZoom(minZoom: number) {
    this.props.minZoom = minZoom
  }

  public get maxZoom(): number {
    return this.props.maxZoom
  }

  public set maxZoom(maxZoom: number) {
    this.props.maxZoom = maxZoom
  }

  public get fileSizeBytes(): number | undefined {
    return this.props.fileSizeBytes
  }

  public set fileSizeBytes(fileSizeBytes: number | undefined) {
    this.props.fileSizeBytes = fileSizeBytes
  }

  public get deleted(): boolean {
    return this.props.deleted
  }

  public set deleted(deleted: boolean) {
    this.props.deleted = deleted
  }

  public get synced(): boolean {
    return this.props.synced
  }

  public set synced(synced: boolean) {
    this.props.synced = synced
  }
}
